import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import AdminDashboard from './components/AdminDashboard';
import AdminFeedback from './components/AdminFeedback';
import AdminMovieSearch from './components/AdminMovieSearch';
import AdminPage from './components/AdminPage';
import AdminUserDetail from './components/AdminUserDetail';
import ExplorePage from './components/ExplorePage';
import FriendStatsPage from './components/FriendStatsPage';
import FriendsPage from './components/FriendsPage';
import Inbox from './components/Inbox';
import Login from './components/Login';
import MovieDetail from './components/MovieDetail';
import MyStatsPage from './components/MyStatsPage';
import PersonMoviesPage from './components/PersonMoviesPage';
import PlaylistDetail from './components/PlaylistDetail';
import PlaylistsHomePage from './components/PlaylistsHomePage';
import ProtectedRoute from './components/ProtectedRoute';
import PullToRefresh from './components/PullToRefresh';
import Sidebar from './components/Sidebar';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;

// Layout wrapper for authenticated pages
function AuthenticatedLayout({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <PullToRefresh>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={wide ? 'container-wide' : 'container'}>
        <div className="header">
          <div className="header-left">
            <button
              className="hamburger-button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div>
              <Link to="/" className="header-title-link">
                <h1>Movie Playlists</h1>
              </Link>
              <p>Create curated movie playlists with AI</p>
            </div>
          </div>
          <div className="header-nav">
            <Inbox />
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="user-avatar"
                onClick={() => setSidebarOpen(true)}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div
                className="user-avatar placeholder"
                onClick={() => setSidebarOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
            )}
          </div>
        </div>
        {children}
      </div>
    </PullToRefresh>
  );
}

function PlaylistsPage() {
  return (
    <AuthenticatedLayout>
      <PlaylistsHomePage />
    </AuthenticatedLayout>
  );
}

function PlaylistPage() {
  return (
    <AuthenticatedLayout>
      <PlaylistDetail />
    </AuthenticatedLayout>
  );
}

function MoviePage() {
  return (
    <AuthenticatedLayout>
      <MovieDetail />
    </AuthenticatedLayout>
  );
}

function FriendsPageWrapper() {
  return (
    <AuthenticatedLayout>
      <FriendsPage />
    </AuthenticatedLayout>
  );
}

function FriendStatsPageWrapper() {
  return (
    <AuthenticatedLayout>
      <FriendStatsPage />
    </AuthenticatedLayout>
  );
}

function AdminDashboardWrapper() {
  return (
    <AuthenticatedLayout wide>
      <AdminDashboard />
    </AuthenticatedLayout>
  );
}

function AdminPageWrapper() {
  return (
    <AuthenticatedLayout>
      <AdminPage />
    </AuthenticatedLayout>
  );
}

function AdminUserDetailWrapper() {
  return (
    <AuthenticatedLayout>
      <AdminUserDetail />
    </AuthenticatedLayout>
  );
}

function AdminFeedbackWrapper() {
  return (
    <AuthenticatedLayout>
      <AdminFeedback />
    </AuthenticatedLayout>
  );
}

function AdminMovieSearchWrapper() {
  return (
    <AuthenticatedLayout>
      <AdminMovieSearch />
    </AuthenticatedLayout>
  );
}

function ExplorePageWrapper() {
  return (
    <AuthenticatedLayout>
      <ExplorePage />
    </AuthenticatedLayout>
  );
}

function InboxPageWrapper() {
  return (
    <AuthenticatedLayout>
      <Inbox fullPage />
    </AuthenticatedLayout>
  );
}

function StatsPageWrapper() {
  return (
    <AuthenticatedLayout>
      <MyStatsPage />
    </AuthenticatedLayout>
  );
}

function PersonPageWrapper() {
  return (
    <AuthenticatedLayout>
      <PersonMoviesPage />
    </AuthenticatedLayout>
  );
}

// Gate all routing behind auth loading state so the login screen
// never flashes for already-authenticated users (especially on iOS).
function AppRoutes() {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* If already authenticated, redirect away from /login */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <PlaylistsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlists/:id"
        element={
          <ProtectedRoute>
            <PlaylistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/movies/:tmdbId"
        element={
          <ProtectedRoute>
            <MoviePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <FriendsPageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends/:id/stats"
        element={
          <ProtectedRoute>
            <FriendStatsPageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminPageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute>
            <AdminUserDetailWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute>
            <AdminFeedbackWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/movies"
        element={
          <ProtectedRoute>
            <AdminMovieSearchWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <ProtectedRoute>
            <ExplorePageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <InboxPageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <StatsPageWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/person/:personId"
        element={
          <ProtectedRoute>
            <PersonPageWrapper />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className="container">
        <div className="card">
          <p>Error: VITE_GOOGLE_CLIENT_ID is not configured.</p>
        </div>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="container">
          <div className="card">
            <p>Something went wrong. Please refresh the page.</p>
          </div>
        </div>
      }
    >
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  );
}
